import { useEffect, useState } from 'react';

export type BuilderLoaderPhase = 'build' | 'run' | 'exit';

export interface BuilderLoaderTimings {
  /** ms after mount when every piece has landed and the "ready" pass starts. */
  runAt: number;
  /** ms after mount when the finished illustration starts lifting away. */
  exitAt: number;
  /** ms after mount when the next cycle begins. */
  restartAt: number;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Drives the build → run → exit loop shared by the builder-page loading skeletons. `cycle` changes
 * on every restart so the caller can re-key its illustration, which remounts it and replays every
 * CSS drop-in from scratch. Stays idle (phase 'build', so the static finished state) when the
 * user prefers reduced motion.
 */
export default function useBuilderLoaderCycle({ runAt, exitAt, restartAt }: BuilderLoaderTimings) {
  const [cycle, setCycle] = useState(0);
  const [phase, setPhase] = useState<BuilderLoaderPhase>('build');

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;

    const timers = [
      setTimeout(() => setPhase('run'), runAt),
      setTimeout(() => setPhase('exit'), exitAt),
      setTimeout(() => {
        setPhase('build');
        setCycle(c => c + 1);
      }, restartAt),
    ];
    return () => timers.forEach(clearTimeout);
  }, [cycle, runAt, exitAt, restartAt]);

  return { cycle, phase };
}
